<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <div class="pm-tabs" role="group" aria-label="Filtrar por estado">
    @for (section of statusSections; track section.status) {
      <button type="button" class="pm-tab" [attr.aria-pressed]="activeFilter() === section.status"
        (click)="activeFilter.set(section.status)">
        {{ section.label }}
        <span class="pm-tab__count">{{ getPromosByStatus(section.status).length }}</span>
      </button>
    }
  </div>
  <button type="button" class="pm-btn pm-btn--primary inline-flex shrink-0 items-center justify-center gap-2" (click)="openCreateModal()">
    <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    Crear promoción
  </button>
</div>

<!-- Loading -->
@if (loading() && promotions().length === 0) {
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" role="status" aria-label="Cargando promociones">
    @for (i of [1, 2, 3]; track i) {
      <div class="pm-coupon animate-pulse">
        <div class="pm-coupon__stub"><div class="h-8 w-16 rounded bg-gray-200 dark:bg-gray-800"></div></div>
        <div class="pm-coupon__body">
          <div class="mb-2 h-4 w-32 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="h-3 w-full rounded bg-gray-100 dark:bg-gray-800/70"></div>
        </div>
      </div>
    }
  </div>
}

<!-- Empty -->
@else if (filteredList().length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">{{ emptyState().title }}</h3>
    <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">{{ emptyState().text }}</p>
    @if (activeFilter() === 'active' || promotions().length === 0) {
      <button type="button" class="pm-btn pm-btn--primary" (click)="openCreateModal()">Crear promoción</button>
    }
  </div>
}

<!-- Coupons -->
@else {
  <ul class="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" [class.opacity-60]="loading()">
    @for (promo of filteredList(); track promo.id) {
      @let time = timeInfo(promo);
      <li>
        <button type="button" class="pm-coupon pm-coupon--action" [attr.data-status]="promo.status" [attr.data-type]="promo.discountType"
          (click)="selectPromotion(promo)" [attr.aria-label]="promo.name + ', ' + discountText(promo) + ', ' + statusLabel(promo.status)">
          <span class="pm-coupon__stub" aria-hidden="true">
            <span class="pm-coupon__value">{{ discountValue(promo) }}</span>
            <span class="pm-coupon__unit">{{ promo.discountType === 'percentage' ? 'de descuento' : 'menos' }}</span>
          </span>
          <span class="pm-coupon__body">
            <span class="pm-coupon__head">
              <span class="pm-coupon__name">{{ promo.name }}</span>
            </span>
            <span class="pm-coupon__scope">{{ servicesLabel(promo) }}</span>
            <span class="pm-coupon__time">
              <span class="pm-coupon__time-text">{{ time.text }}</span>
              @if (promo.status === 'active') {
                <span class="pm-progress" aria-hidden="true"><span [style.width.%]="time.percent"></span></span>
              }
            </span>
          </span>
        </button>
      </li>
    }
  </ul>
}

<!-- Detail drawer -->
@if (selectedPromotion(); as promo) {
  <div class="pm-backdrop" (click)="selectedPromotion.set(null)" aria-hidden="true"></div>
  <div class="pm-panel" role="dialog" aria-modal="true" aria-labelledby="pm-detail-title">
    <div class="pm-panel__head">
      <div class="min-w-0">
        <p class="pm-status" [attr.data-status]="promo.status">{{ statusLabel(promo.status) }}</p>
        <h2 id="pm-detail-title" class="pm-panel__title">{{ promo.name }}</h2>
      </div>
      <button #detailCloseBtn type="button" class="pm-icon-btn" (click)="selectedPromotion.set(null)" aria-label="Cerrar detalle">
        <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="pm-panel__body">
      <div class="pm-coupon pm-coupon--hero" [attr.data-status]="promo.status" [attr.data-type]="promo.discountType">
        <span class="pm-coupon__stub">
          <span class="pm-coupon__value">{{ discountValue(promo) }}</span>
          <span class="pm-coupon__unit">{{ promo.discountType === 'percentage' ? 'de descuento' : 'menos' }}</span>
        </span>
        <span class="pm-coupon__body">
          <span class="pm-coupon__desc">{{ promo.description }}</span>
        </span>
      </div>

      <dl class="pm-facts">
        <div>
          <dt>Vigencia</dt>
          <dd>{{ promo.startDate | date: 'd MMM y' }} al {{ promo.endDate | date: 'd MMM y' }}
            <span class="pm-facts__sub">{{ timeInfo(promo).text }}</span>
          </dd>
        </div>
        <div>
          <dt>Aplica a</dt>
          <dd>
            @if (promo.services.length === 0) {
              Todos los servicios
            } @else {
              <span class="flex flex-wrap gap-1.5">
                @for (service of promo.services; track service) {
                  <span class="pm-chip">{{ service }}</span>
                }
              </span>
            }
          </dd>
        </div>
        <div>
          <dt>Para</dt>
          <dd>{{ audienceLabel(promo.audience) }}</dd>
        </div>
        <div>
          <dt>Se anuncia por</dt>
          <dd>
            @if (promo.channels.length) {
              {{ channelsText(promo) }}
            } @else {
              Ningún canal
            }
          </dd>
        </div>
        <div>
          <dt>Usos</dt>
          <dd>
            {{ promo.redemptions }}@if (promo.goal) {<span> de {{ promo.goal }}</span>}
            @if (promo.goal) {
              <span class="pm-progress pm-progress--goal mt-2" role="img" [attr.aria-label]="goalProgress(promo) + ' % de la meta'">
                <span [style.width.%]="goalProgress(promo)"></span>
              </span>
            }
          </dd>
        </div>
      </dl>

      <section class="pm-state" aria-labelledby="pm-state-title">
        <h3 id="pm-state-title" class="pm-state__title">Cambiar estado</h3>
        <div class="flex flex-wrap gap-2">
          @if (promo.status !== 'active') {
            <button type="button" class="pm-btn pm-btn--ghost" (click)="setStatus(promo, 'active')">Activar ahora</button>
          }
          @if (promo.status !== 'scheduled') {
            <button type="button" class="pm-btn pm-btn--ghost" (click)="setStatus(promo, 'scheduled')">Programar</button>
          }
          @if (promo.status !== 'expired') {
            <button type="button" class="pm-btn pm-btn--ghost" (click)="setStatus(promo, 'expired')">Finalizar</button>
          }
        </div>
      </section>
    </div>

    <div class="pm-panel__foot">
      <button type="button" class="pm-btn pm-btn--danger-text" (click)="deletePromotion()">Eliminar</button>
      <button type="button" class="pm-btn pm-btn--primary inline-flex items-center gap-2" (click)="openEditModal()">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
        </svg>
        Editar promoción
      </button>
    </div>
  </div>
}

<!-- Create / edit -->
<app-modal [open]="modalOpen()" [title]="isEditMode() ? 'Editar promoción' : 'Crear promoción'" [size]="'md-1'"
  [bodyComponent]="PromotionAddForm" (confirm)="onModalConfirm($event)" [closeOnBackdrop]="false"
  [bodyInputs]="modalBodyInputs" (close)="closeModal()"
  [confirmButtonText]="isEditMode() ? 'Guardar cambios' : 'Crear promoción'" [isSaving]="saving()">
</app-modal>

<!-- Delete confirmation -->
<app-confirm-modal [(open)]="confirmDeleteOpen"
  title="Eliminar promoción"
  [subtitle]="'Vas a eliminar \'' + (selectedPromotion()?.name ?? '') + '\'. Los clientes dejarán de verla y no podrás recuperarla.'"
  cancelText="No, volver" confirmText="Sí, eliminar" variant="danger"
  (confirm)="onConfirmDelete()">
</app-confirm-modal>
